import React from 'react';
import { Earth, ListOrdered, Scale, CalendarSync, ShieldAlert, Binoculars } from 'lucide-react';
import UpgradePage from 'src/shared/templates/upgrade-page/UpgradePage';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import analyticsTracking from 'src/services/analytics-service';
import gnrHeroBg from 'src/assets/images/gnr-hero-bg.webp';

/** Global Nature Risk's own HubSpot form (portal/region match the shared default). */
const GNR_HUBSPOT_FORM_ID = '94329224-77fa-450e-82ee-ff4200e9c971';

const FEATURES = [
    {
        icon: Earth,
        title: 'Global Enterprise Scale',
        body: 'Screen thousands of physical assets simultaneously, anywhere in the world, across any sector.',
    },
    {
        icon: ListOrdered,
        title: 'Consistent 0–1 Risk Scoring',
        body: 'Rank global assets by impact, dependency and nature condition within days.',
    },
    {
        icon: Scale,
        title: 'Framework-Aligned Disclosures',
        body: "Export audit-ready evidence that's fully aligned with TNFD, CSRD E4 and other standards.",
    },
    {
        icon: CalendarSync,
        title: 'Quarterly Data Refreshes',
        body: 'Track changing risk profiles over time with updates synced to financial reporting cycles.',
    },
    {
        icon: ShieldAlert,
        title: 'Early Risk Flagging',
        body: 'Spot regulatory and reputational hazards before they impact project permitting or financing.',
    },
    {
        icon: Binoculars,
        title: 'Connected Platform Intelligence',
        body: 'Prioritise high-risk sites for deeper investigation using native eDNA or bioacoustics.',
    },
];

const GnrUpgradePage = () => {
    const { currentCustomerName } = useCustomer();

    const handleBookCallClick = () =>
        analyticsTracking.trackEvent({
            action: 'upgrade_subscription_click_from_global_nature_risk',
            category: 'upgrade-subscription-link',
            label: currentCustomerName,
        });

    return (
        <UpgradePage
            testId='gnr-upgrade-page'
            breadcrumbs={[{ label: currentCustomerName }, { label: 'Global Nature Risk' }]}
            heroImage={gnrHeroBg}
            heroLayout='full-bleed'
            badgeLabel='Upgrade to Global Nature Risk'
            title='A 0-1 risk score for every site in your portfolio'
            description='Know where value lies in going deeper. Global Nature Risk gives every site in your portfolio a consistent 0-1 score, so you can see where exposure is concentrated, direct investigation where it counts and reprioritise as your portfolio shifts.'
            features={FEATURES}
            ctaTitle='Ready to uncover hidden liabilities before they impact your balance sheet?'
            ctaDescription='Upgrade to Global Nature Risk and start tracking exposure across your entire portfolio with the home of nature intelligence.'
            webinar={{
                label: 'Watch the webinar',
                href: 'https://watch.getcontrast.io/register/naturemetrics-nature-risk-is-business-risk?utm_source=nm+platform&utm_medium=nm+platform&utm_campaign=nature+risk+is+business+risk+webinar',
            }}
            hubSpotForm={{ formId: GNR_HUBSPOT_FORM_ID }}
            onBookCallClick={handleBookCallClick}
        />
    );
};

export default GnrUpgradePage;
